import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Divider } from "@mui/material";
import classNames from "classnames";

import { CustomButton, IButtonVariant } from "../../../components/CustomButton";
import { CustomModal } from "../../../components/CustomModal";
import { IconNameEnum, IconTypeEnum } from "../../../components/Icon";
import { TextInput } from "../../../components/TextInput";
import { MAX_PLATE_NUMBER_LENGTH } from "../../../configs/plateNumber";
import { useQrScanner } from "../../../hooks/useQRScanner";
import { getVisitEditPlateNumber, getVisitEditTotalPriceForFinishedEvents } from "../../../selectors/visitEdit";
import { IModal } from "../../../types/modal";
import { formatCurrency } from "../../../utils/formatCurrency";

import styles from "./VerifyPaidVisitModal.module.scss";

type VerifyPaidVisitModalProps = IModal & {
  handleContinue: (plateNumber: string, qrCodeToken: string) => void;
};

export const VERIFY_PAID_VISIT_MODAL_ID = "verify-paid-visit-modal";

export const VerifyPaidVisitModal: React.FC<VerifyPaidVisitModalProps> = ({
  isModalVisible,
  toggleModalVisibility,
  handleContinue,
}) => {
  const { t } = useTranslation();

  const totalPriceForFinishedEvents = useSelector(getVisitEditTotalPriceForFinishedEvents);
  const plateNumberDefault = useSelector(getVisitEditPlateNumber);
  const { qrResult, startQrScanner, setQrResult } = useQrScanner();

  const [plateNumber, setPlateNumber] = useState<string>("");

  const handleVerifyCitizen = () => {
    startQrScanner();
  };

  useEffect(() => {
    if (qrResult) {
      handleContinue(plateNumber, qrResult);
      toggleModalVisibility();

      setQrResult("");
    }
  }, [qrResult]);

  useEffect(() => {
    if (isModalVisible) {
      setPlateNumber(plateNumberDefault || "");
    }
  }, [isModalVisible]);

  return (
    <CustomModal
      id={VERIFY_PAID_VISIT_MODAL_ID}
      isModalVisible={isModalVisible}
      toggleModalVisibility={toggleModalVisibility}
      title="verifyPaidVisitModal.title"
      contentGap={12}
    >
      <p className={styles.modalText}>{t("verifyPaidVisitModal.enterPlateNumberText")}</p>
      <TextInput
        placeHolder={t("verifyPaidVisitModal.plateNumberPlaceholder")}
        value={plateNumber}
        onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
        maxLength={MAX_PLATE_NUMBER_LENGTH}
        icon={IconNameEnum.truck}
        iconType={IconTypeEnum.grey}
        iconSize={24}
      />

      <div className={styles.spacer}></div>
      <Divider />
      <div className={styles.spacer}></div>

      <h5 className={styles.modalHeader}>{t("verifyPaidVisitModal.verifyCitizenTitle")}</h5>

      <div>
        <p className={styles.modalText}>{t("verifyPaidVisitModal.verifyCitizenText")}</p>
        <p className={styles.modalText}>{t("verifyPaidVisitModal.totalPrice")}</p>
        <p className={classNames(styles.modalText, styles.bold)}>{formatCurrency(totalPriceForFinishedEvents)}</p>
      </div>

      <div className={styles.buttonsContainer}>
        <CustomButton
          text={t("verifyPaidVisitModal.verifyCitizen")}
          fullWidth
          onClick={handleVerifyCitizen}
          // For now plate_number is not required
          // disabled={plateNumber.length < MIN_PLATE_NUMBER_LENGTH}
        />
        <CustomButton
          text={t("verifyPaidVisitModal.backToEditing")}
          onClick={toggleModalVisibility}
          variant={IButtonVariant.neutral}
          fullWidth
        />
      </div>
    </CustomModal>
  );
};
